import { IClientAllocationDetailed } from '~/services/api/clientAllocation/types';

/**
 * Detects which allocations have been changed by comparing adjustedFormValues with clientAllocations.
 * Returns only the allocations that have changes (memo changes or client list changes).
 *
 * @param adjustedFormValues - The adjusted form values with keys like clients_USERID_ALLOCATIONID and memo_CLIENTID_USERID_ALLOCATIONID
 * @param clientAllocations - The original client allocations to compare against
 * @returns Array of changed allocations from clientAllocations
 */
const findChangedAllocations = (
  adjustedFormValues: Record<string, unknown>,
  clientAllocations: IClientAllocationDetailed[],
  dirtyFields?: Record<string, unknown>,
  isGlobalUser?: boolean,
): IClientAllocationDetailed[] => {
  const changedAllocationIds = new Set<number>();
  // Track which allocations have clients_* fields AND changed (for memo checking)
  const editedAllocationIds = new Set<number>();

  // First pass: Process all clients_* fields to detect client list changes
  // Match by: allocationId and userId
  Object.keys(adjustedFormValues).forEach(key => {
    if (key.startsWith('clients_')) {
      const parts = key.split('_');
      const userId = parts[1];
      const allocationId = parts[2];

      // Skip if no allocationId (user-level fields)
      if (!allocationId) return;

      const formClients = (adjustedFormValues[key] as number[]) || [];
      const allocationIdNum = Number(allocationId);
      const userIdNum = Number(userId);

      // Strictly match: allocationId and userId
      const allocation = clientAllocations.find(
        a => a.id === allocationIdNum && a.user.id === userIdNum,
      );

      if (allocation) {
        const originalClientIds = allocation.user.clients?.map(c => c.id) || [];

        // Check if clients array changed
        // Case 1: Empty array when original had clients (all removed) - this is a change
        // Case 2: Different length
        // Case 3: Same length but different clients
        const sortedFormClients = new Set([...formClients].sort((a, b) => a - b) as number[]);
        const sortedOriginalClients = new Set(
          [...originalClientIds].sort((a, b) => a - b) as number[],
        );

        // Check if clients array changed (including empty arrays)
        // This will catch: [] vs [15844] (different lengths)
        const clientsChanged =
          sortedFormClients.size !== sortedOriginalClients.size ||
          JSON.stringify(Array.from(sortedFormClients)) !==
            JSON.stringify(Array.from(sortedOriginalClients));

        if (clientsChanged) {
          // Clients array changed - mark as edited and changed
          editedAllocationIds.add(allocation.id);
          changedAllocationIds.add(allocation.id);
        } else {
          // Clients array didn't change, but allocation exists in form
          // Mark as edited so we can check memo changes (memo-only edits)
          editedAllocationIds.add(allocation.id);
        }
      }
    }
  });
  // Second pass: Process all memo_* fields to detect memo changes
  // Only check memo changes for allocations that were actually edited (have clients_* field)
  Object.keys(dirtyFields).forEach(key => {
    if (key.startsWith('memo_')) {
      const parts = key.split('_');
      const clientId = Number(parts[1]);
      const userId = parts[2];
      const allocationId = parts[3];

      // Skip if no allocationId
      if (!allocationId) return;

      const allocationIdNum = Number(allocationId);
      const userIdNum = Number(userId);

      // Only check memo changes for allocations that were actually edited
      if (!editedAllocationIds.has(allocationIdNum)) {
        return;
      }

      // Strictly match: allocationId, userId, and clientId
      const allocation = clientAllocations.find(
        a => a.id === allocationIdNum && a.user.id === userIdNum,
      );

      if (allocation) {
        // Verify client exists in this specific allocation for this user
        const client = allocation.user.clients?.find(c => c.id === clientId);
        if (client) {
          // All three identifiers match: allocationId, userId, clientId
          // Check if memo changed
          const formMemo = (adjustedFormValues[key] as string) || '';
          const originalMemo = client.memo || '';

          // Check if memo changed (don't trim to preserve empty strings)
          const memoChanged = (formMemo || '') !== (originalMemo || '');

          if (memoChanged) {
            changedAllocationIds.add(allocation.id);
          }
        }
      }
    }
  });
  // Third pass: detect per-client teamId changes (global user only, since TeamsField
  // is rendered exclusively for global user).
  // NOTE: we cannot rely on dirtyFields here because TeamsField's mount effect calls
  // setValue(fieldName, primary) without shouldDirty, which breaks dirty tracking via
  // Controller in some cases. So we compare form value vs the allocation's original
  // team.id directly.
  if (isGlobalUser) {
    clientAllocations.forEach(allocation => {
      if (allocation.isArchived || allocation.isCrisis) return;
      const originalTeamId = allocation.team?.id;
      if (!originalTeamId) return;

      const someClientChangedTeam = allocation.user?.clients?.some(client => {
        const key = `teamId_${client.id}_${allocation.user.id}_${allocation.id}`;
        const raw = adjustedFormValues[key];
        if (raw === undefined || raw === null || raw === '') return false;
        const formTeamId = Number(raw);
        if (!formTeamId) return false;
        return formTeamId !== originalTeamId;
      });

      if (someClientChangedTeam) {
        editedAllocationIds.add(allocation.id);
        changedAllocationIds.add(allocation.id);
      }
    });
  }

  // Return only the changed allocations with updated client data
  // Only include allocations that were actually edited (have clients_* field AND changed)
  return clientAllocations
    .filter(
      allocation =>
        // Only include if: 1) it's marked as changed AND 2) it was actually edited (in editedAllocationIds)
        changedAllocationIds.has(allocation.id) && editedAllocationIds.has(allocation.id),
    )
    .map(allocation => {
      const allocationKey = `clients_${allocation.user.id}_${allocation.id}`;
      // Get form clients - key exists because we filtered above
      const formClients = (adjustedFormValues[allocationKey] as number[]) || [];

      // Create updated allocation with new clients array
      // If formClients is empty (all clients removed), return empty array
      const updatedClients =
        formClients.length === 0
          ? []
          : formClients.map(clientId => {
              // Find the original client data
              const originalClient = allocation.user.clients?.find(c => c.id === clientId);

              // Get updated memo from form values
              // Match by: clientId, userId, allocationId
              const memoKey = `memo_${clientId}_${allocation.user.id}_${allocation.id}`;
              const updatedMemo = (adjustedFormValues[memoKey] as string) || '';

              // Return client with updated memo, or create minimal client object if newly added
              if (originalClient) {
                return {
                  ...originalClient,
                  memo: updatedMemo,
                };
              }

              // Newly added client - create minimal object
              return {
                id: clientId,
                firstName: '',
                lastName: '',
                photo: '',
                photoThumbnail: '',
                dateOfBirth: '',
                medicaId: '',
                isArchived: false,
                status: 'Active',
                memo: updatedMemo,
                gender: { id: 0, name: '' },
                coordinates: { lat: 0, lng: 0 },
              };
            });

      return {
        ...allocation,
        user: {
          ...allocation.user,
          clients: updatedClients,
        },
      };
    });
};

export default findChangedAllocations;
